/**
 * 作成素材作成に関する関数群 ====================================================================================
 */

import * as constants from "./constants";
import * as tools from "./tools";
import * as operationPosition from "./operationPosition";
import * as createjs from "createjs-module";

/**
 * ディスプレイオブジェクトを作成する関数。
 * @param  {} stage
 * @param  {} createProperty // 共通化するためにdisplayObjectと構造を合わせておく
 * @param  {} callback
 * @return {DisplayObject}
 */
export const createMaterial = (stage, createProperty, callback = undefined) => {
  // 配置制限
  const displayObjectsContainer = stage.getChildByName(
    "displayObjectsContainer"
  );
  if (displayObjectsContainer.numChildren - 1 >= constants.decoMaxNum) {
    return;
  }

  switch (createProperty.decoData.type) {
    case constants.decoType.normalText:
      createProperty.font = constants.defaultFont;
      return createDisplayText(stage, createProperty, callback);
    case constants.decoType.monoImage:
      createProperty.font = constants.defaultIconFont;
      return createDisplayText(stage, createProperty, callback);
    case constants.decoType.outlineText:
      createProperty.font = constants.defaultFont;
      return createDisplayOutlineText(stage, createProperty, callback);
    case constants.decoType.normalImage:
      return createDisplayBitmap(stage, createProperty, callback);
  }
  return undefined;
};

/**
 * bitmapに関する関数群 ====================================================================================
 */

/**
 * ディスプレイオブジェクト（画像系）を作成する関数。（作成素材など）
 * @param  {} stage
 * @param  {} imageProperty
 * @param  {} callback
 * @return {DisplayObject}
 */
const createDisplayBitmap = (stage, imageProperty, callback = undefined) => {
  const bitmap = new createjs.Bitmap(imageProperty.decoData.src);

  // 画像ロード後に親コンテナを設定するとコンテナへの追加順が変わってしまうため、
  // 先に追加しておく
  if (imageProperty.targetContainer !== undefined) {
    const targetContainer = stage.getChildByName(imageProperty.targetContainer);
    targetContainer.addChild(bitmap);
  } else {
    const displayObjectsContainer = stage.getChildByName(
      "displayObjectsContainer"
    );
    displayObjectsContainer.addChild(bitmap);
  }

  // 読み込みの時間があるので、一旦非表示にして読み込み後に表示
  bitmap.visible = false;

  bitmap.image.onload = () => {
    bitmap.visible = true;

    initDisplayObject(stage, bitmap, imageProperty);

    if (callback) {
      callback(stage, bitmap);
    }
  };

  return bitmap;
};

/**
 * END======================================================================================================================
 */

/**
 * テキストオブジェクトに関する関数群 ====================================================================================
 */

/**
 * ディスプレイオブジェクト（テキスト）を作成する関数。（作成素材など）
 * @param  {} stage
 * @param  {} textProperty
 * @param  {} callback
 * @return {DisplayObject}
 */
const createDisplayText = (stage, textProperty, callback) => {
  const inputText = new createjs.Text(
    textProperty.decoData.text,
    textProperty.font,
    textProperty.decoData.color
  );
  inputText.textAlign = textProperty.decoData.textAlign;

  initDisplayObject(stage, inputText, textProperty);

  if (callback) {
    callback(stage, inputText);
  }

  return inputText;
};

/**
 * ディスプレイオブジェクト（縁付きテキスト）を作成する関数。（作成素材など）
 * @param  {} stage
 * @param  {} textProperty
 * @param  {} callback
 * @return {DisplayObject}
 */
const createDisplayOutlineText = (stage, textProperty, callback) => {
  // outlineTextだけだと、縁しか描画されないため、その上にinputTextを重ねる
  const inputText = new createjs.Text(
    textProperty.decoData.text,
    textProperty.font,
    textProperty.decoData.color
  );
  inputText.textAlign = textProperty.decoData.textAlign;
  inputText.name = "innerText";
  // outlineTextのデフォルト色は黒
  const outlineText = new createjs.Text(
    textProperty.decoData.text,
    textProperty.font,
    textProperty.decoData.outlineColor
  );
  outlineText.textAlign = textProperty.decoData.textAlign;
  // outlineの太さは固定値
  outlineText.outline = 10;
  outlineText.name = "outlineText";
  const textContainer = new createjs.Container();

  textContainer.addChild(outlineText);
  textContainer.addChild(inputText);

  initDisplayObject(stage, textContainer, textProperty);

  if (callback) {
    callback(stage, textContainer);
  }

  return textContainer;
};

/**
 * END======================================================================================================================
 */

/**
 * displayObjectを作成時に初期化する関数
 * @param  {} stage
 * @param  {} displayObject
 * @param  {} property
 */
const initDisplayObject = (stage, displayObject, property) => {
  // anchorを左上から中心に変更
  // textAlignによってなぜか補正されるので、そこは無視する
  // https://www.createjs.com/docs/easeljs/files/easeljs_display_Text.js.html#l257
  if (property.decoData.textAlign) {
    switch (property.decoData.textAlign) {
      case "center":
        displayObject.regX = 0;
        break;
      case "right":
        displayObject.regX = -displayObject.getBounds().width / 2;
        break;
      default:
        displayObject.regX = displayObject.getBounds().width / 2;
        break;
    }
  } else {
    displayObject.regX = displayObject.getBounds().width / 2;
  }
  displayObject.regY = displayObject.getBounds().height / 2;

  // 大きさ変更
  displayObject.scaleX = displayObject.scaleY = operationPosition.getDefaultScale(
    displayObject,
    property.decoData.type
  );

  // 中央に配置
  operationPosition.setPositionDefault(stage, displayObject);

  // 当たり判定領域の計算（透明部分は無視されてしまうので）
  tools.setHitArea(displayObject);

  if (!displayObject.parent) {
    if (property.targetContainer !== undefined) {
      const targetContainer = stage.getChildByName(property.targetContainer);
      targetContainer.addChild(displayObject);
    } else {
      const displayObjectsContainer = stage.getChildByName(
        "displayObjectsContainer"
      );
      displayObjectsContainer.addChild(displayObject);
    }
  }

  // json保存用のデータ（タイプ登録など）
  displayObject.decoData = property.decoData;

  return;
};
